import { useGetMagazineAuthorQuery } from '@/hooks/magazine/magazine.gql.impl';
import { AffiliationType, DoctorCareerType, GetMagazineAuthorQuery } from '@/types/bff/gql-schema';
import { QueryClient } from '@tanstack/react-query';
import { EAffiliationType, EDoctorCareer, MagazineAuthorModel } from '../model/magazine';

interface UseGetMagazineAuthorProps {
  authorPublicId: string;
}

const parseAffiliationType = (affiliationType: AffiliationType): EAffiliationType => {
  switch (affiliationType) {
    case 'DOCTORNOW':
      return EAffiliationType.Doctornow;
    case 'HOSPITAL':
      return EAffiliationType.Hospital;
    case 'PHARMACY':
      return EAffiliationType.Pharmacy;
    default:
      return EAffiliationType.Unknown;
  }
};

const parseCareerType = (careerType: DoctorCareerType): EDoctorCareer => {
  switch (careerType) {
    case DoctorCareerType.Current:
      return EDoctorCareer.Current;
    case DoctorCareerType.Past:
      return EDoctorCareer.Past;
    case DoctorCareerType.Graduation:
      return EDoctorCareer.Graduation;
    case DoctorCareerType.Completion:
      return EDoctorCareer.Completion;
    default:
      return EDoctorCareer.Unknown;
  }
};

const parseGetMagazineAuthor = (data: GetMagazineAuthorQuery): MagazineAuthorModel | null => {
  if (!data.magazineAuthor) {
    return null;
  }

  return new MagazineAuthorModel({
    affiliation: data.magazineAuthor.affiliation
      ? {
          affiliationType: parseAffiliationType(data.magazineAuthor.affiliation.affiliationType),
          affiliationPid: data.magazineAuthor.affiliation.affiliationPid,
          name: data.magazineAuthor.affiliation.name,
        }
      : null,
    career: data.magazineAuthor.career.map((career) => ({
      careerType: parseCareerType(career.careerType),
      content: career.content,
      id: career.id,
      isRepresentative: career.isRepresentative,
    })),
    name: data.magazineAuthor.name,
    profileDescription: data.magazineAuthor.profileDescription ?? '',
    profileImage: data.magazineAuthor.profileImage ?? '',
    publicId: data.magazineAuthor.publicId,
  });
};

const useGetMagazineAuthor = ({ authorPublicId }: UseGetMagazineAuthorProps) => {
  const { data: magazineAuthor } = useGetMagazineAuthorQuery(
    {
      authorPublicId,
    },
    {
      select: parseGetMagazineAuthor,
    }
  );

  return {
    magazineAuthor,
  };
};

export default useGetMagazineAuthor;

useGetMagazineAuthor.getKey = (authorPublicId: string) => {
  return useGetMagazineAuthorQuery.getKey({ authorPublicId });
};

useGetMagazineAuthor.prefetchQuery = async (queryClient: QueryClient, authorPublicId: string) => {
  await queryClient.prefetchQuery(
    useGetMagazineAuthorQuery.getKey({ authorPublicId }),
    useGetMagazineAuthorQuery.fetcher({ authorPublicId })
  );
};
